/**
 * Data Context
 *
 * Manages all item data and CRUD operations with Google Drive.
 * Implements optimistic updates for responsive UI.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { nanoid } from 'nanoid';
import { googleDrive } from '@/services/googleDrive';
import { createItemWriteScheduler, withCrossTabWriteLock } from '@/services/itemWriteScheduler';
import { broadcastChange } from '@/services/syncChannel';
import { mergeItemArrays } from '@/services/mergeItems';
import { getPendingSyncIds } from '@/services/syncTracking';
import { useAuth } from './AuthContext';
import { getCurrentISODate } from '@/utils/dates';
import { getTagColor } from '@/utils/colors';
import type {
  Item,
  ItemType,
  ItemsState,
  LinkItem,
  PodcastItem,
  NoteItem,
  IdeaItem,
  FileItem,
  TaskItem,
  AppConfig,
  TagDefinition,
  OnboardingState,
} from '@/types';

// ============================================
// TYPES
// ============================================

type ItemInput<T extends Item> = Omit<T, 'id' | 'createdAt' | 'updatedAt'>;

interface DataContextValue {
  // State
  config: AppConfig;
  items: ItemsState;
  allItems: Item[];
  isLoading: boolean;
  error: string | null;

  // CRUD operations
  createItem: <T extends Item>(item: ItemInput<T>) => Promise<T>;
  updateItem: (item: Item, options?: { skipTimestampUpdate?: boolean }) => void;
  deleteItem: (item: Item) => Promise<void>;

  // Tag operations
  addTag: (name: string) => TagDefinition;
  removeTag: (name: string) => void;
  getTagColor: (name: string) => string;

  // File operations
  uploadFile: (file: File) => Promise<FileItem>;

  // Onboarding
  updateOnboarding: (updates: Partial<OnboardingState>) => void;

  // Refresh
  refreshData: () => Promise<void>;
  silentRefresh: () => Promise<void>;
}

// ============================================
// INITIAL STATE
// ============================================

const initialConfig: AppConfig = {
  version: '1.0.0',
  tags: [{ name: 'inbox', color: '#FEF3C7' }],
  lastSyncedAt: '',
};

const initialItems: ItemsState = {
  links: [],
  podcasts: [],
  notes: [],
  ideas: [],
  files: [],
  tasks: [],
};

const ITEM_FILENAMES: Record<keyof ItemsState, string> = {
  links: 'links.json',
  podcasts: 'podcasts.json',
  notes: 'notes.json',
  ideas: 'ideas.json',
  files: 'files.json',
  tasks: 'tasks.json',
};

// ============================================
// CONTEXT
// ============================================

const DataContext = createContext<DataContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface DataProviderProps {
  children: ReactNode;
}

export function DataProvider({ children }: DataProviderProps) {
  const { user } = useAuth();
  const [config, setConfig] = useState<AppConfig>(initialConfig);
  const [items, setItems] = useState<ItemsState>(initialItems);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Merge tracking refs
  const dirtyItemIdsRef = useRef<Set<string>>(new Set());
  const deletedItemIdsRef = useRef<Set<string>>(new Set());
  const isSyncingRef = useRef(false);
  const itemWriteSchedulerRef = useRef<ReturnType<typeof createItemWriteScheduler>>();

  if (!itemWriteSchedulerRef.current) {
    itemWriteSchedulerRef.current = createItemWriteScheduler({
      getTrackingIds: () => ({
        dirtyIds: new Set(dirtyItemIdsRef.current),
        deletedIds: new Set(deletedItemIdsRef.current),
      }),
      onError: (filename, err) => {
        console.warn(`Merged write failed for ${filename}:`, err);
      },
      onWriteComplete: () => {
        broadcastChange('items');
      },
      readRemote: (filename) => googleDrive.readJsonFresh<Item[]>(filename, []),
      writeRemote: (filename, data) => googleDrive.writeJsonImmediate(filename, data),
    });
  }

  // Compute all items array
  const allItems: Item[] = [
    ...items.links,
    ...items.podcasts,
    ...items.notes,
    ...items.ideas,
    ...items.files,
    ...items.tasks,
  ];

  const updateConfig = useCallback((updater: (current: AppConfig) => AppConfig) => {
    setConfig((current) => {
      const next = updater(current);
      googleDrive.writeJson('config.json', next);
      return next;
    });
  }, []);

  // Load data when user logs in
  useEffect(() => {
    if (!user) {
      itemWriteSchedulerRef.current?.dispose();
      setItems(initialItems);
      setConfig(initialConfig);
      dirtyItemIdsRef.current.clear();
      deletedItemIdsRef.current.clear();
      return;
    }

    void loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    return () => {
      itemWriteSchedulerRef.current?.dispose();
    };
  }, []);

  // Load all data from Google Drive
  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load all files in parallel
      const [loadedConfig, links, podcasts, notes, ideas, files, tasks] = await Promise.all([
        googleDrive.readJson<AppConfig>('config.json', initialConfig),
        googleDrive.readJson<LinkItem[]>('links.json', []),
        googleDrive.readJson<PodcastItem[]>('podcasts.json', []),
        googleDrive.readJson<NoteItem[]>('notes.json', []),
        googleDrive.readJson<IdeaItem[]>('ideas.json', []),
        googleDrive.readJson<FileItem[]>('files.json', []),
        googleDrive.readJson<TaskItem[]>('tasks.json', []),
      ]);

      setConfig({ ...loadedConfig, lastSyncedAt: getCurrentISODate() });
      setItems({ links, podcasts, notes, ideas, files, tasks });
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data from Google Drive');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Silent refresh - merges remote data with local state instead of replacing it.
  // Preserves local dirty edits, respects local deletes, accepts new remote items.
  // Uses readJsonFresh to bypass browser cache and get latest data from Drive.
  const silentRefresh = useCallback(async () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    try {
      // Fetch latest from Drive
      const [
        loadedConfig,
        remoteLinks,
        remotePodcasts,
        remoteNotes,
        remoteIdeas,
        remoteFiles,
        remoteTasks,
      ] = await Promise.all([
        googleDrive.readJsonFresh<AppConfig>('config.json', initialConfig),
        googleDrive.readJsonFresh<LinkItem[]>('links.json', []),
        googleDrive.readJsonFresh<PodcastItem[]>('podcasts.json', []),
        googleDrive.readJsonFresh<NoteItem[]>('notes.json', []),
        googleDrive.readJsonFresh<IdeaItem[]>('ideas.json', []),
        googleDrive.readJsonFresh<FileItem[]>('files.json', []),
        googleDrive.readJsonFresh<TaskItem[]>('tasks.json', []),
      ]);

      const dirtyIds = new Set(dirtyItemIdsRef.current);
      const deletedIds = new Set(deletedItemIdsRef.current);

      // Config uses simple last-write-wins (conflicts rare and low-stakes)
      setConfig({ ...loadedConfig, lastSyncedAt: getCurrentISODate() });

      // Merge each item type
      const remoteByType = {
        links: remoteLinks,
        podcasts: remotePodcasts,
        notes: remoteNotes,
        ideas: remoteIdeas,
        files: remoteFiles,
        tasks: remoteTasks,
      } as const;

      const typeKeys: (keyof ItemsState)[] = [
        'links',
        'podcasts',
        'notes',
        'ideas',
        'files',
        'tasks',
      ];
      // Use setItems callback to get current local state (avoids stale closure)
      let nextDirtyIds = new Set<string>();
      let nextDeletedIds = new Set<string>();
      setItems((prev) => {
        const next = { ...prev };
        const writeBackPromises: Promise<void>[] = [];
        const retainedDirtyIds = new Set<string>();
        const retainedDeletedIds = new Set<string>();

        for (const key of typeKeys) {
          const { merged, needsWriteBack } = mergeItemArrays(
            prev[key],
            remoteByType[key],
            dirtyIds,
            deletedIds
          );

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (next as any)[key] = merged;

          const pendingSyncIds = getPendingSyncIds(merged, remoteByType[key], dirtyIds, deletedIds);
          pendingSyncIds.dirtyIds.forEach((id) => retainedDirtyIds.add(id));
          pendingSyncIds.deletedIds.forEach((id) => retainedDeletedIds.add(id));

          if (needsWriteBack) {
            writeBackPromises.push(
              withCrossTabWriteLock(ITEM_FILENAMES[key], () =>
                googleDrive.writeJsonImmediate(ITEM_FILENAMES[key], merged)
              )
                .then(() => {
                  broadcastChange('items');
                })
                .catch((err) => {
                  console.warn(`Write-back failed for ${ITEM_FILENAMES[key]}:`, err);
                })
            );
          }
        }

        // Fire write-backs outside the state setter (don't block render)
        if (writeBackPromises.length > 0) {
          void Promise.all(writeBackPromises);
        }

        nextDirtyIds = retainedDirtyIds;
        nextDeletedIds = retainedDeletedIds;

        return next;
      });

      dirtyItemIdsRef.current = nextDirtyIds;
      deletedItemIdsRef.current = nextDeletedIds;
    } catch (err) {
      // Silent fail - don't show error for background refresh
      console.warn('Silent refresh failed:', err);
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  // ============================================
  // CRUD OPERATIONS
  // ============================================

  const createItem = useCallback(
    <T extends Item>(itemInput: ItemInput<T>): Promise<T> => {
      const now = getCurrentISODate();
      const newItem = {
        ...itemInput,
        id: nanoid(10),
        createdAt: now,
        updatedAt: now,
      } as T;

      const itemType = newItem.type;
      const typeKey = `${itemType}s` as keyof ItemsState;

      dirtyItemIdsRef.current.add(newItem.id);

      // Update lastSyncedAt
      updateConfig((current) => ({ ...current, lastSyncedAt: now }));

      // Optimistic update + write to Drive with CURRENT state
      setItems((prev) => {
        const updatedArray = [...prev[typeKey], newItem];

        itemWriteSchedulerRef.current?.schedule(ITEM_FILENAMES[typeKey], updatedArray);

        return {
          ...prev,
          [typeKey]: updatedArray,
        };
      });

      return Promise.resolve(newItem);
    },
    [updateConfig]
  );

  const updateItem = useCallback(
    (item: Item, options?: { skipTimestampUpdate?: boolean }) => {
      const now = getCurrentISODate();
      const updatedItem = options?.skipTimestampUpdate ? item : { ...item, updatedAt: now };
      const itemType = item.type;
      const typeKey = `${itemType}s` as keyof ItemsState;

      dirtyItemIdsRef.current.add(item.id);

      // Update lastSyncedAt
      updateConfig((current) => ({ ...current, lastSyncedAt: now }));

      // Optimistic update + write to Drive with CURRENT state
      setItems((prev) => {
        const updatedArray = prev[typeKey].map((i) => (i.id === item.id ? updatedItem : i));

        itemWriteSchedulerRef.current?.schedule(ITEM_FILENAMES[typeKey], updatedArray);

        return {
          ...prev,
          [typeKey]: updatedArray,
        };
      });

    },
    [updateConfig]
  );

  const deleteItem = useCallback(
    async (item: Item) => {
      const now = getCurrentISODate();
      const itemType = item.type;
      const typeKey = `${itemType}s` as keyof ItemsState;

      deletedItemIdsRef.current.add(item.id);

      // For file items, also delete from Drive uploads
      if (item.type === 'file') {
        try {
          await googleDrive.deleteFile(item.googleDriveFileId);
        } catch (err) {
          console.error('Failed to delete file from Drive:', err);
        }
      }

      // Update lastSyncedAt
      updateConfig((current) => ({ ...current, lastSyncedAt: now }));

      // Optimistic update + write to Drive with CURRENT state
      setItems((prev) => {
        const updatedArray = prev[typeKey].filter((i) => i.id !== item.id);

        itemWriteSchedulerRef.current?.schedule(ITEM_FILENAMES[typeKey], updatedArray);

        return {
          ...prev,
          [typeKey]: updatedArray,
        };
      });

    },
    [updateConfig]
  );

  // ============================================
  // TAG OPERATIONS
  // ============================================

  const addTag = useCallback(
    (name: string): TagDefinition => {
      const existing = config.tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (existing) return existing;

      const newTag: TagDefinition = {
        name,
        color: getTagColor(name).bg,
      };

      updateConfig((current) => ({
        ...current,
        tags: [...current.tags, newTag],
        lastSyncedAt: getCurrentISODate(),
      }));
      broadcastChange('config');

      return newTag;
    },
    [config.tags, updateConfig]
  );

  const removeTag = useCallback(
    (name: string) => {
      updateConfig((current) => ({
        ...current,
        tags: current.tags.filter((t) => t.name !== name),
        lastSyncedAt: getCurrentISODate(),
      }));
      broadcastChange('config');
    },
    [updateConfig]
  );

  const getTagColorFn = useCallback(
    (name: string): string => {
      const tag = config.tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
      if (tag) return tag.color;
      return getTagColor(name).bg;
    },
    [config.tags]
  );

  // ============================================
  // ONBOARDING
  // ============================================

  const updateOnboarding = useCallback(
    (updates: Partial<OnboardingState>) => {
      updateConfig((currentConfig) => {
        const currentOnboarding: OnboardingState = currentConfig.onboarding ?? {
          completedWelcome: false,
          dismissedChecklist: false,
          completedSteps: [],
        };

        const newOnboarding: OnboardingState = {
          ...currentOnboarding,
          ...updates,
          completedSteps: updates.completedSteps
            ? [...new Set([...currentOnboarding.completedSteps, ...updates.completedSteps])]
            : currentOnboarding.completedSteps,
        };

        return {
          ...currentConfig,
          onboarding: newOnboarding,
          lastSyncedAt: getCurrentISODate(),
        };
      });
      broadcastChange('config');
    },
    [updateConfig]
  );

  // ============================================
  // FILE OPERATIONS
  // ============================================

  const uploadFile = useCallback(
    async (file: File): Promise<FileItem> => {
      const { fileId, webViewLink } = await googleDrive.uploadFile(file);

      const fileItem: ItemInput<FileItem> = {
        type: 'file',
        title: file.name,
        description: '',
        tags: ['inbox'],
        isFavorite: false,
        originalFileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        googleDriveFileId: fileId,
        webViewLink,
        personalNotes: '',
      };

      return createItem(fileItem);
    },
    [createItem]
  );

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value: DataContextValue = {
    config,
    items,
    allItems,
    isLoading,
    error,
    createItem,
    updateItem,
    deleteItem,
    addTag,
    removeTag,
    getTagColor: getTagColorFn,
    uploadFile,
    updateOnboarding,
    refreshData: loadAllData,
    silentRefresh,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

// ============================================
// HOOK
// ============================================

// eslint-disable-next-line react-refresh/only-export-components
export function useData(): DataContextValue {
  const context = useContext(DataContext);

  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }

  return context;
}

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Get items filtered by type
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useItemsByType<T extends Item>(type: ItemType): T[] {
  const { items } = useData();
  const typeKey = `${type}s` as keyof ItemsState;
  return items[typeKey] as T[];
}

/**
 * Get a single item by ID
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useItemById(id: string): Item | undefined {
  const { allItems } = useData();
  return allItems.find((item) => item.id === id);
}

/**
 * Get items count by type
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useItemCounts(): Record<ItemType, number> {
  const { items } = useData();
  return {
    link: items.links.length,
    podcast: items.podcasts.length,
    note: items.notes.length,
    idea: items.ideas.length,
    file: items.files.length,
    task: items.tasks.length,
  };
}
